import React, { useState } from 'react'
import { Button, Header, Segment, Modal, Icon, Message } from 'semantic-ui-react'
import { useHistory } from 'react-router-dom'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import { IWizardProject } from '../../types/wizardData'
import { IProject } from '../../types/project'
import { executeTransfer } from '../../../budget/project-import/api'
import { ExecutionLogEntry } from '../../../budget/project-import/types'
import { TransferExecutionLogDisplay } from '../../../budget/project-import/components/TransferExecutionLogDisplay'
import FormattedTotal from '../../../budget/components/FormattedTotal'

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
  getProjects?: (queryParams?: any) => void
  getLoggedInUser?: () => Promise<any>
  getProject: (id: number) => Promise<IProject>
  createProjectSuccess: (project: IProject) => void
  filters?: any
}

/**
 * Budget Import Finish Step Component
 *
 * Final step that:
 * - Shows import summary
 * - Executes budget transfer
 * - Displays execution logs in modal
 * - Navigates to created project
 *
 * @example
 * <BudgetImportFinishStep {...wizardProps} />
 */
export const BudgetImportFinishStep: React.FC<Props> = (props) => {
  const { data, setWizardStep, onClose, open, wizardFinishedSteps, getProjects, getLoggedInUser, getProject, createProjectSuccess, filters } = props
  const history = useHistory()
  const enablePolling = import.meta.env.VITE_ENABLE_BUDGET_TRANSFER_POLLING === 'true'

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [createdProjectId, setCreatedProjectId] = useState<number | null>(null)
  const [executionLog, setExecutionLog] = useState<ExecutionLogEntry[]>([])
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [pollingMessage, setPollingMessage] = useState<string>('')

  /**
   * Polls projects API until the created project appears in the list
   * @param projectId - ID of the created project
   * @param maxAttempts - Maximum number of polling attempts (default: 200)
   * @param intervalMs - Interval between attempts in milliseconds (default: 10000)
   */
  const pollForProject = async (projectId: number, maxAttempts = 200, intervalMs = 10000): Promise<boolean> => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      setPollingMessage(`Verifying project visibility (attempt ${attempt}/${maxAttempts})...`)
      console.log(`[BudgetImportFinishStep] Polling attempt ${attempt}/${maxAttempts} for project ${projectId}`)

      if (getLoggedInUser) {
        await getLoggedInUser()
      }

      if (getProjects) {
        const response: any = await getProjects(filters)
        if (response && response.filtered) {
          const projectExists = response.filtered.some((p: any) => p.id === projectId)

          if (projectExists) {
            console.log(`[BudgetImportFinishStep] Project ${projectId} found in list after ${attempt} attempts`)
            setPollingMessage('')
            return true
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, intervalMs))
    }

    console.warn(`[BudgetImportFinishStep] Project ${projectId} not found after ${maxAttempts} attempts`)
    setPollingMessage('')
    return false
  }

  /**
   * Executes budget transfer
   */
  const handleExecute = async (): Promise<void> => {
    setLoading(true)
    setError(null)
    setExecutionLog([])
    setPollingMessage('')

    const budgetId = data.budgetImportSource!.budgetId
    const wizardData = {
      budget_id: budgetId,
      project_name: data.name,
      permissions: (data.permissions || 'restricted') as 'open' | 'restricted',
      billing_procedure: (data.billing_procedure || 'scheduled_payment') as 'scheduled_payment' | 'billing_by_tasks' | 'billing_on_actual'
    }

    const result = await executeTransfer(budgetId, wizardData)

    if (result.success && result.project_id) {
      setExecutionLog(result.execution_log || [])

      console.log('[BudgetImportFinishStep] Transfer successful, fetching project data...', {
        projectId: result.project_id,
        enablePolling
      })

      const project = await getProject(result.project_id)

      if (Object.keys(project).length !== 0) {
        createProjectSuccess(project)
        setCreatedProjectId(result.project_id)
        console.log('[BudgetImportFinishStep] Project added to Redux, showing success message')

        if (getLoggedInUser) {
          getLoggedInUser()
        }

        if (enablePolling) {
          console.log('[BudgetImportFinishStep] Polling enabled, verifying project visibility...')
          const projectFound = await pollForProject(result.project_id)
          if (!projectFound) {
            console.warn('[BudgetImportFinishStep] Polling timeout - project may not be visible yet')
          }
        }

        console.log('[BudgetImportFinishStep] Reloading projects list in background...')
        setTimeout(() => {
          if ((window as any).reloadProjectsList) {
            (window as any).reloadProjectsList()
          }
        }, 1000)
      } else {
        setError('Failed to fetch project data. Please refresh the page.')
        console.error('[BudgetImportFinishStep] getProject returned empty object')
      }
    } else {
      setError(result.errors.map(e => e.message).join(', '))
      setExecutionLog(result.execution_log || [])
    }

    setLoading(false)
  }

  /**
   * Navigates to created project dashboard
   */
  const goToProject = (): void => {
    onClose()
    window.location.href = `/projects/${createdProjectId}/dashboard`
  }

  /**
   * Handles back navigation
   */
  const goToPrevStep = (): void => {
    setWizardStep('budget-import-select-billing')
  }

  /**
   * Renders wizard content
   */
  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <Segment textAlign="center" style={{ padding: '40px' }}>
          <Icon name="spinner" loading size="huge" />
          <Header as="h3">{pollingMessage || 'Importing Budget...'}</Header>
          <p>{pollingMessage ? 'Waiting for project to appear in your projects list...' : 'Transferring budget data to new project...'}</p>
        </Segment>
      )
    }

    if (createdProjectId) {
      return (
        <Segment textAlign="center" style={{ padding: '40px' }}>
          <Icon name="check circle" size="huge" color="green" />
          <Header as="h2">Budget Imported Successfully!</Header>
          <p><strong>Project ID:</strong> #{createdProjectId}</p>
          <p><strong>Project Name:</strong> {data.name}</p>

          <div style={{ marginTop: '30px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <Button onClick={() => setShowLogsModal(true)} basic>
              View Logs
            </Button>
            <Button onClick={() => onClose()}>
              Back to Projects List
            </Button>
            <Button onClick={goToProject} primary>
              Go to Project Dashboard
            </Button>
          </div>
        </Segment>
      )
    }

    return (
      <>
        <Segment>
          <Header as="h3">Transfer Summary</Header>
          <p><strong>Source Budget:</strong> {data.budgetImportSource!.budgetName}</p>
          <p><strong>Client:</strong> {data.budgetImportSource!.clientName}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <strong>Total Budget:</strong>
            <FormattedTotal amount={data.budgetImportSource!.totalBudget} showLabel={false} />
          </div>
          <p><strong>New Project Name:</strong> {data.name}</p>
          <p><strong>Permissions:</strong> {data.permissions || 'restricted'}</p>
          <p><strong>Billing Procedure:</strong> {data.billing_procedure || 'scheduled_payment'}</p>
        </Segment>

        {error && (
          <Message negative>
            <Message.Header>Transfer Failed</Message.Header>
            <p>{error}</p>
          </Message>
        )}
      </>
    )
  }

  /**
   * Renders wizard footer
   */
  const renderFooter = (): JSX.Element | undefined => {
    if (loading || createdProjectId) {
      return undefined
    }

    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPrevStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, color: '#747678', borderColor: '#BEC8C3', marginRight: '1rem' }}
        />
        <Button
          primary={true}
          disabled={loading}
          size='small'
          onClick={handleExecute}
          content={loading ? 'Importing...' : 'Finish'}
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  return (
    <>
      <WizardComponent
        headerContent="Review & Import Budget"
        removeSteps={true}
        finishedSteps={wizardFinishedSteps}
        open={open}
        close={onClose}
        content={renderContent()}
        footer={renderFooter()}
        contentPadding='0 48px 2rem'
        actionPadding='20px 48px'
      />

      <Modal
        open={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        size="fullscreen"
        style={{
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh'
        }}
      >
        <Modal.Header>Transfer Execution Log</Modal.Header>
        <Modal.Content scrolling>
          <TransferExecutionLogDisplay executionLog={executionLog} />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowLogsModal(false)}>Close</Button>
        </Modal.Actions>
      </Modal>
    </>
  )
}

export default BudgetImportFinishStep
